/**
 * IceReasoningCard
 *
 * The founder-facing ICE card: the composite score and band, all three
 * dimensions with their reasoning and evidence, the weakest dimension, the
 * next step, and what changed since the last evaluation.
 *
 * Renders the card's **structured** fields (never `card.markdown` as well —
 * the two paths carry identical information and must not be mixed in one
 * view), and never `current.assessments`, which is internal detail.
 *
 * @module @ideoz/ui/components/molecules/IceReasoningCard
 */

import React from 'react';
import { ArrowDownRight, ArrowRight, ArrowUpRight, TriangleAlert } from 'lucide-react';
import type { IceBand, IceCardDimension } from '@ideoz/core';
import { cn } from '../../lib/utils';
import type { IceReasoningCardProps } from '../../types/Ice.types';

/**
 * Band tint only. The label text always comes from `card.band_label` — the
 * backend owns that mapping.
 */
const BAND_TINT: Record<IceBand, string> = {
  strong: 'bg-success-50 text-success-700 border-success-200',
  worth_pursuing: 'bg-blue-50 text-blue-700 border-blue-200',
  needs_work: 'bg-warning-50 text-warning-700 border-warning-200',
  park: 'bg-gray-100 text-gray-600 border-gray-200',
};

const DEFAULT_TINT = 'bg-gray-100 text-gray-700 border-gray-200';

/** Fills the dimension bar. 1–10, so the score doubles as a percentage. */
const barWidth = (score: number) => `${Math.max(0, Math.min(10, score)) * 10}%`;

const DimensionRow: React.FC<{ dimension: IceCardDimension; isWeakest: boolean }> = ({
  dimension,
  isWeakest,
}) => (
  <li
    className={cn(
      'rounded-lg border p-3',
      isWeakest ? 'border-warning-200 bg-warning-25' : 'border-gray-200 bg-white'
    )}
    data-testid={`ice-dimension-${dimension.dimension}`}
  >
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-sm font-medium text-gray-700">{dimension.label}</span>
      <span className="text-sm font-semibold text-gray-900">{dimension.score.toFixed(1)}</span>
    </div>

    <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-gray-100">
      <div
        className={cn('h-full rounded-full', isWeakest ? 'bg-warning-400' : 'bg-primary-500')}
        style={{ width: barWidth(dimension.score) }}
      />
    </div>

    {/* Always shown: a founder must be able to see why a scope ranks where it does. */}
    <p className="mt-2 text-sm text-gray-600">{dimension.reasoning}</p>

    <p className="mt-1.5 text-xs text-gray-400">
      {dimension.evidence_sources.length > 0
        ? dimension.evidence_sources.join(' · ')
        : /* An unsourced score is information — say so rather than hide the row. */
          'No source recorded yet'}
    </p>
  </li>
);

/** Directional icon for a change line, read off its leading delta sign. */
const changeIcon = (line: string) => {
  if (/\(\+/.test(line)) return ArrowUpRight;
  if (/\(-/.test(line)) return ArrowDownRight;
  return ArrowRight;
};

export const IceReasoningCard: React.FC<IceReasoningCardProps> = ({
  card,
  band = null,
  className = '',
  testId = 'ice-reasoning-card',
}) => {
  const tint = band ? (BAND_TINT[band] ?? DEFAULT_TINT) : DEFAULT_TINT;
  const weakestLabel = card.weakest_label?.toLowerCase() ?? '';
  const changeLines = card.change_lines ?? [];

  return (
    <section
      data-testid={testId}
      data-evaluation-id={card.evaluation_id}
      className={cn('flex flex-col gap-3', className)}
    >
      {/* Score + band + what to do about it */}
      <div className={cn('rounded-xl border px-3.5 py-3', tint)}>
        <div className="flex items-baseline gap-2">
          <span className="text-2xl font-semibold leading-none">{card.ice.toFixed(1)}</span>
          <span className="text-sm font-medium">{card.band_label}</span>
        </div>
        <p className="mt-1.5 text-sm opacity-90">{card.action}</p>
      </div>

      <ul className="flex flex-col gap-2">
        {card.dimensions.map((dimension) => (
          <DimensionRow
            key={dimension.dimension}
            dimension={dimension}
            // `weakest_label` can name two dimensions ("Confidence + Ease").
            isWeakest={!!weakestLabel && weakestLabel.includes(dimension.label.toLowerCase())}
          />
        ))}
      </ul>

      {/* The point of the card, not a footer. */}
      <div className="rounded-lg border border-primary-200 bg-primary-25 p-3">
        <p className="text-[11px] font-medium uppercase tracking-wide text-primary-700">
          Weakest: {card.weakest_label}
        </p>
        <p className="mt-1 text-sm text-gray-700">{card.next_step}</p>
      </div>

      {(card.change_summary || changeLines.length > 0) && (
        <div className="rounded-lg border border-gray-200 bg-gray-25 p-3" data-testid="ice-change">
          {card.change_summary && (
            <p className="text-sm font-medium text-gray-700">{card.change_summary}</p>
          )}
          {changeLines.length > 0 && (
            <ul className="mt-1.5 flex flex-col gap-1">
              {changeLines.map((line) => {
                const Icon = changeIcon(line);
                return (
                  <li key={line} className="flex items-start gap-1.5 text-sm text-gray-600 mb-2">
                    <Icon className="mt-0.5 h-3 w-3 shrink-0 text-gray-400" strokeWidth={2} />
                    <span>{line}</span>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}

      {/* ICE is prioritization input, not a verdict — never a build/kill gate. */}
      <p className="flex items-start gap-1.5 text-[11px] text-gray-400">
        <TriangleAlert className="mt-0.5 h-3 w-3 shrink-0" strokeWidth={2} aria-hidden />
        <span>
          A prioritization signal, not a verdict — a low score is a reason to sequence, not to stop.
        </span>
      </p>
    </section>
  );
};

export default IceReasoningCard;
